import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Modal estandarizado para crear/editar/ver elementos
 * Proporciona una interfaz consistente para todas las operaciones CRUD
 */
const StandardModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'medium',
  type = 'default',
  loading = false,
  className = '',
  closeOnOverlayClick = true,
  closeOnEscape = true
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Manejar el foco y accesibilidad
  useEffect(() => {
    if (isOpen) {
      // Guardar el elemento activo anterior
      previousActiveElement.current = document.activeElement;

      // Enfocar el modal
      if (modalRef.current) {
        modalRef.current.focus();
      }

      // Prevenir scroll del body
      document.body.style.overflow = 'hidden';
    } else {
      // Restaurar el foco anterior
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }

      // Restaurar scroll del body
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Manejar tecla Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && closeOnEscape && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeOnEscape, onClose]);

  // Manejar clic en overlay
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  // Manejar foco dentro del modal (trap focus)
  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  // Detectar si es móvil
  const isMobile = window.innerWidth <= 768;

  // Estilos inline para móvil
  const overlayStyle = isMobile ? {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    padding: 0,
    margin: 0,
    zIndex: 9999,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch'
  } : {};

  const modalStyle = isMobile ? {
    position: 'relative',
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    margin: 0,
    padding: 0,
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  } : {};

  return (
    <div
      className={`standard-modal-overlay ${className}`}
      style={overlayStyle}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className={`standard-modal standard-modal-${size} standard-modal-${type} ${loading ? 'loading' : ''}`}
        style={modalStyle}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {/* Header del modal */}
        <div className="standard-modal-header">
          <h2 id="modal-title" className="modal-title">
            {title}
          </h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Cerrar modal"
            type="button"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* Contenido del modal */}
        <div className="standard-modal-body">
          {loading ? (
            <div className="modal-loading">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <span>Cargando...</span>
              </div>
            </div>
          ) : (
            children
          )}
        </div>

        {/* Footer del modal */}
        {footer && (
          <div className="standard-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

StandardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'extra-large']),
  type: PropTypes.oneOf(['default', 'create', 'edit', 'view', 'delete', 'warning']),
  loading: PropTypes.bool,
  className: PropTypes.string,
  closeOnOverlayClick: PropTypes.bool,
  closeOnEscape: PropTypes.bool
};

export default StandardModal;
